import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

// Criteria page (amended model):
//  - Agents/brokers edit STRUCTURE (add category, add catalog sub-criterion + target,
//    rename, delete, reorder). Buyers never edit structure.
//  - Importance (weights) are buyer-only by default; hidden from agents/brokers unless
//    the buyer has delegated. The buyer owns a delegation toggle + sees a change log of
//    agent-made weight edits.
export default function BuyerCriteria() {
  const { buyerId } = useParams();
  const { user } = useAuth();

  const [buyer, setBuyer] = useState(null);
  const [hierarchy, setHierarchy] = useState([]);
  const [flags, setFlags] = useState({ weights_delegated: false, weights_hidden: false, can_edit_weights: false, can_edit_structure: false });
  const [catalog, setCatalog] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [showAddParent, setShowAddParent] = useState(false);
  const [parentName, setParentName] = useState('');
  const [showAddSub, setShowAddSub] = useState(null); // parent_id
  const [subForm, setSubForm] = useState({ catalog_key: '', target_operator: '', target_value: '' });
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [showLog, setShowLog] = useState(false);

  const isBuyer = user?.role === 'buyer';

  const loadData = useCallback(async () => {
    try {
      const [buyerRes, critRes] = await Promise.all([
        authFetch(`${API}/api/buyers/${buyerId}`),
        authFetch(`${API}/api/criteria/buyer/${buyerId}/hierarchy`),
      ]);
      if (buyerRes.ok) setBuyer(await buyerRes.json());
      if (critRes.ok) {
        const data = await critRes.json();
        setHierarchy(data.criteria || []);
        setFlags({
          weights_delegated: !!data.weights_delegated,
          weights_hidden: !!data.weights_hidden,
          can_edit_weights: !!data.can_edit_weights,
          can_edit_structure: !!data.can_edit_structure,
        });
      }
      // catalog for structure editors
      if (['super_admin', 'broker', 'agent'].includes(user?.role)) {
        const catRes = await authFetch(`${API}/api/criteria/catalog`);
        if (catRes.ok) setCatalog(await catRes.json());
      }
      const auditRes = await authFetch(`${API}/api/criteria/buyer/${buyerId}/weight-audit`);
      if (auditRes.ok) setAudit(await auditRes.json());
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [buyerId, user?.role]);

  useEffect(() => { loadData(); }, [loadData]);

  const clear = () => { setSuccess(''); setError(''); };
  const flash = (msg, isErr = false) => { isErr ? setError(msg) : setSuccess(msg); setTimeout(clear, 4000); };

  // ---- keys already present (block duplicate catalog attributes) ----
  const usedKeys = new Set();
  hierarchy.forEach(p => { if (p.catalog_key) usedKeys.add(p.catalog_key); (p.sub_criteria || []).forEach(s => s.catalog_key && usedKeys.add(s.catalog_key)); });
  const availableCatalog = catalog.filter(c => !usedKeys.has(c.key));
  const selectedCatalog = catalog.find(c => c.key === subForm.catalog_key);

  // ---- delegation (buyer only) ----
  const toggleDelegation = async (enabled) => {
    clear();
    try {
      const res = await authFetch(`${API}/api/criteria/buyer/${buyerId}/delegation`, {
        method: 'PUT', body: JSON.stringify({ enabled }),
      });
      if (res.ok) { flash(enabled ? 'Your agent can now view and adjust your importance ratings.' : 'Agent access to your importance ratings revoked.'); loadData(); }
      else flash('Could not update the setting.', true);
    } catch (e) { flash('Network error', true); }
  };

  // ---- weights ----
  const updateWeightLocal = (id, w, isParent) => {
    setHierarchy(prev => prev.map(p => {
      if (isParent && p.id === id) return { ...p, weight: w };
      return { ...p, sub_criteria: p.sub_criteria.map(s => s.id === id ? { ...s, weight: w } : s) };
    }));
  };
  const saveWeights = async () => {
    setSaving(true); clear();
    try {
      const updates = [];
      hierarchy.forEach(p => { updates.push({ id: p.id, weight: p.weight }); p.sub_criteria.forEach(s => updates.push({ id: s.id, weight: s.weight })); });
      const res = await authFetch(`${API}/api/criteria/buyer/${buyerId}/bulk-weights`, { method: 'PUT', body: JSON.stringify({ updates }) });
      if (res.ok) { const d = await res.json(); flash(`Saved ${d.updated} importance ratings — board rescored.`); loadData(); }
      else { const e = await res.json(); flash(e.detail || 'Failed to save', true); }
    } catch (e) { flash('Network error', true); } finally { setSaving(false); }
  };

  // ---- structure ----
  const addParent = async (e) => {
    e.preventDefault(); clear();
    try {
      const res = await authFetch(`${API}/api/criteria`, { method: 'POST', body: JSON.stringify({ buyer_id: parseInt(buyerId), name: parentName, category_type: 'parent', weight: 0.1, parent_id: null }) });
      if (res.ok) { flash(`Category "${parentName}" added`); setParentName(''); setShowAddParent(false); loadData(); }
      else { const er = await res.json(); flash(er.detail || 'Failed to add category', true); }
    } catch (e) { flash('Network error', true); }
  };
  const addSub = async (e, parentId) => {
    e.preventDefault(); clear();
    if (!subForm.catalog_key) { flash('Pick an attribute to add.', true); return; }
    const body = { buyer_id: parseInt(buyerId), category_type: 'sub', parent_id: parentId, weight: 0.1, catalog_key: subForm.catalog_key };
    if (selectedCatalog?.target && subForm.target_value !== '') {
      body.target_operator = subForm.target_operator || selectedCatalog.target.default_operator;
      body.target_value = parseFloat(subForm.target_value);
    }
    try {
      const res = await authFetch(`${API}/api/criteria`, { method: 'POST', body: JSON.stringify(body) });
      if (res.ok) { const d = await res.json(); flash(`Added "${d.name}" — board rescored (${d.properties_rescored} homes).`); setShowAddSub(null); setSubForm({ catalog_key: '', target_operator: '', target_value: '' }); loadData(); }
      else { const er = await res.json(); flash(er.detail || 'Failed to add', true); }
    } catch (e) { flash('Network error', true); }
  };
  const renameCriterion = async (id) => {
    clear();
    try {
      const res = await authFetch(`${API}/api/criteria/${id}`, { method: 'PUT', body: JSON.stringify({ name: editName }) });
      if (res.ok) { flash('Renamed'); setEditingId(null); setEditName(''); loadData(); }
      else { const er = await res.json(); flash(er.detail || 'Failed', true); }
    } catch (e) { flash('Network error', true); }
  };
  const deleteCriterion = async (id, name, isParent) => {
    if (!window.confirm(isParent ? `Delete "${name}" and all its sub-criteria (and their grades)?` : `Delete "${name}" (and its grades)?`)) return;
    clear();
    try {
      const res = await authFetch(`${API}/api/criteria/${id}`, { method: 'DELETE' });
      if (res.ok) { flash(`"${name}" deleted — board rescored.`); loadData(); }
      else { const er = await res.json(); flash(er.detail || 'Failed to delete', true); }
    } catch (e) { flash('Network error', true); }
  };
  const move = async (list, index, dir, isParent, parentId) => {
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    const a = list[index], b = list[j];
    const order = [{ id: a.id, display_order: b.display_order ?? j }, { id: b.id, display_order: a.display_order ?? index }];
    // ensure distinct orders
    order[0].display_order = j; order[1].display_order = index;
    try {
      const res = await authFetch(`${API}/api/criteria/buyer/${buyerId}/reorder`, { method: 'PUT', body: JSON.stringify({ order }) });
      if (res.ok) loadData();
    } catch (e) { /* ignore */ }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center text-[#00D4FF]">Loading criteria...</div>;

  const canStruct = flags.can_edit_structure;
  const canWeight = flags.can_edit_weights;
  const hidden = flags.weights_hidden;
  const backTo = user?.role === 'super_admin' ? '/buyers' : (isBuyer ? '/match-board' : '/buyers');

  const WeightCell = ({ item, isParent }) => {
    if (hidden) return <span className="text-gray-500 text-xs italic shrink-0">importance hidden</span>;
    if (!canWeight) return <span className="text-[#00D4FF] font-medium text-sm w-10 text-right shrink-0">{Math.round((item.weight || 0) * 100)}%</span>;
    return (
      <div className="flex items-center gap-2 w-full md:w-56 shrink-0">
        <input type="range" min="0" max="1" step="0.05" value={item.weight || 0}
          onChange={(e) => updateWeightLocal(item.id, parseFloat(e.target.value), isParent)}
          className="flex-1 accent-[#00D4FF] h-1.5" />
        <span className="text-[#00D4FF] font-medium text-sm w-10 text-right">{Math.round((item.weight || 0) * 100)}%</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-6 pb-12">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3 mb-4">
          <div className="min-w-0">
            <Link to={backTo} className="text-[#00D4FF] text-sm hover:text-[#00E676] mb-1 inline-block">← Back</Link>
            <h1 className="text-xl md:text-2xl font-bold text-white truncate">{buyer?.name}'s Criteria</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">
              {canStruct ? 'Add, rename, remove and reorder criteria. ' : ''}
              {isBuyer ? 'Set the importance of each criterion.' : (hidden ? 'Importance ratings are set by the buyer.' : 'You may adjust importance ratings for this buyer.')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {canStruct && <button onClick={() => { setShowAddParent(!showAddParent); setShowAddSub(null); }} className="px-3 py-2 bg-[#243B55] text-[#00D4FF] font-medium rounded-lg hover:bg-[#2d4a6a] text-sm">+ Category</button>}
            {canWeight && <button onClick={saveWeights} disabled={saving} className="px-4 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 text-sm">{saving ? 'Saving...' : 'Save Importance'}</button>}
          </div>
        </div>

        {success && <div className="bg-green-900/50 border border-green-600 text-green-200 px-4 py-2.5 rounded-lg mb-3 text-sm">{success}</div>}
        {error && <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-2.5 rounded-lg mb-3 text-sm">{error}</div>}

        {/* Delegation — buyer's own control */}
        {isBuyer && (
          <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/20 p-4 mb-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={flags.weights_delegated} onChange={(e) => toggleDelegation(e.target.checked)} className="mt-1 w-5 h-5 accent-[#00D4FF] shrink-0" />
              <span className="min-w-0">
                <span className="text-white font-medium text-sm block">Allow my agent to view and adjust my importance ratings</span>
                <span className="text-gray-400 text-xs">Off by default. You can turn this off anytime and always keep full control. Every change your agent makes is logged below.</span>
              </span>
            </label>
            {audit.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#546E7A]/20">
                <button onClick={() => setShowLog(!showLog)} className="text-[#00D4FF] text-xs hover:text-[#00E676]">{showLog ? 'Hide' : 'Show'} change log ({audit.length})</button>
                {showLog && (
                  <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto">
                    {audit.map(a => (
                      <div key={a.id} className="text-xs text-gray-300 flex flex-wrap gap-x-2">
                        <span className="text-gray-500">{new Date(a.changed_at).toLocaleString()}</span>
                        <span className="text-white">{a.criterion_name}</span>
                        <span className="text-gray-400">{Math.round((a.old_weight || 0) * 100)}% → {Math.round((a.new_weight || 0) * 100)}%</span>
                        <span className="text-gray-500">by {a.changed_by_email || a.changed_by_role}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Delegation status banner for agents/brokers */}
        {!isBuyer && !flags.can_edit_weights && (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 px-4 py-3 mb-4 text-sm text-[#B0BEC5]">
            🔒 Importance ratings are private to the buyer. They can enable “Allow my agent to view and adjust my importance ratings” to delegate.
          </div>
        )}
        {!isBuyer && flags.can_edit_weights && flags.weights_delegated && (
          <div className="bg-[#1A2744] rounded-xl border border-[#00E676]/30 px-4 py-3 mb-4 text-sm text-[#B0BEC5] flex items-center justify-between gap-2 flex-wrap">
            <span>✓ The buyer delegated importance editing to you. Every change is logged for them.</span>
            {audit.length > 0 && <button onClick={() => setShowLog(!showLog)} className="text-[#00D4FF] text-xs hover:text-[#00E676]">{showLog ? 'Hide' : 'View'} log</button>}
          </div>
        )}
        {!isBuyer && showLog && audit.length > 0 && (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 mb-4 space-y-1.5 max-h-56 overflow-y-auto">
            {audit.map(a => (
              <div key={a.id} className="text-xs text-gray-300 flex flex-wrap gap-x-2">
                <span className="text-gray-500">{new Date(a.changed_at).toLocaleString()}</span>
                <span className="text-white">{a.criterion_name}</span>
                <span className="text-gray-400">{Math.round((a.old_weight || 0) * 100)}% → {Math.round((a.new_weight || 0) * 100)}%</span>
                <span className="text-gray-500">by {a.changed_by_email || a.changed_by_role}</span>
              </div>
            ))}
          </div>
        )}

        {/* Add Parent */}
        {showAddParent && canStruct && (
          <form onSubmit={addParent} className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/30 p-4 mb-4 flex flex-col sm:flex-row gap-2">
            <input type="text" value={parentName} onChange={(e) => setParentName(e.target.value)} required placeholder="New category name (e.g. Investment Potential)"
              className="flex-1 min-w-0 px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg text-sm">Add</button>
              <button type="button" onClick={() => setShowAddParent(false)} className="px-3 py-2 text-gray-400 hover:text-white text-sm">Cancel</button>
            </div>
          </form>
        )}

        {/* Hierarchy */}
        <div className="space-y-4">
          {hierarchy.map((parent, pi) => (
            <div key={parent.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
              {/* Parent */}
              <div className="p-4 border-b border-[#546E7A]/20 flex flex-col md:flex-row md:items-center gap-2">
                <div className="flex-1 min-w-0">
                  {editingId === parent.id ? (
                    <div className="flex gap-2">
                      <input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 min-w-0 px-2 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-white text-sm" />
                      <button onClick={() => renameCriterion(parent.id)} className="px-3 py-1.5 bg-[#00E676] text-[#0A0E27] font-semibold rounded text-sm">Save</button>
                      <button onClick={() => setEditingId(null)} className="px-2 text-gray-400 text-sm">✕</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className="text-white font-semibold text-base break-words min-w-0">{parent.name}</h3>
                      {canStruct && (
                        <span className="flex items-center gap-1 shrink-0">
                          <button onClick={() => move(hierarchy, pi, -1, true)} className="text-gray-600 hover:text-[#00D4FF] text-xs" title="Move up">▲</button>
                          <button onClick={() => move(hierarchy, pi, 1, true)} className="text-gray-600 hover:text-[#00D4FF] text-xs" title="Move down">▼</button>
                          <button onClick={() => { setEditingId(parent.id); setEditName(parent.name); }} className="text-gray-500 hover:text-[#00D4FF] text-sm" title="Rename">✎</button>
                          <button onClick={() => deleteCriterion(parent.id, parent.name, true)} className="text-gray-600 hover:text-red-400 text-sm" title="Delete">✕</button>
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="md:w-56"><WeightCell item={parent} isParent={true} /></div>
              </div>

              {/* Subs */}
              <div className="divide-y divide-[#546E7A]/10">
                {(parent.sub_criteria || []).map((sub, si) => (
                  <div key={sub.id} className="px-4 py-2.5 flex flex-col md:flex-row md:items-center gap-2">
                    <div className="flex-1 min-w-0">
                      {editingId === sub.id ? (
                        <div className="flex gap-2">
                          <input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 min-w-0 px-2 py-1 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-white text-sm" />
                          <button onClick={() => renameCriterion(sub.id)} className="px-2.5 py-1 bg-[#00E676] text-[#0A0E27] font-semibold rounded text-xs">Save</button>
                          <button onClick={() => setEditingId(null)} className="px-1 text-gray-400 text-sm">✕</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-gray-200 text-sm break-words min-w-0">{sub.name}</span>
                          {sub.catalog_key && <span className="text-[10px] text-gray-500 border border-[#546E7A]/40 rounded px-1 shrink-0">auto-scored</span>}
                          {canStruct && (
                            <span className="flex items-center gap-1 shrink-0">
                              <button onClick={() => move(parent.sub_criteria, si, -1, false)} className="text-gray-600 hover:text-[#00D4FF] text-xs" title="Move up">▲</button>
                              <button onClick={() => move(parent.sub_criteria, si, 1, false)} className="text-gray-600 hover:text-[#00D4FF] text-xs" title="Move down">▼</button>
                              <button onClick={() => { setEditingId(sub.id); setEditName(sub.name); }} className="text-gray-600 hover:text-[#00D4FF] text-xs" title="Rename">✎</button>
                              <button onClick={() => deleteCriterion(sub.id, sub.name, false)} className="text-gray-700 hover:text-red-400 text-xs" title="Delete">✕</button>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="md:w-56"><WeightCell item={sub} isParent={false} /></div>
                  </div>
                ))}

                {/* Add sub — catalog picker */}
                {canStruct && (showAddSub === parent.id ? (
                  <form onSubmit={(e) => addSub(e, parent.id)} className="px-4 py-3 bg-[#0A0E27]/50 flex flex-col gap-2">
                    <select value={subForm.catalog_key} onChange={(e) => setSubForm({ catalog_key: e.target.value, target_operator: '', target_value: '' })} required
                      className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                      <option value="">Choose a scorable attribute…</option>
                      {availableCatalog.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                    {selectedCatalog?.target && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 shrink-0">Optional target:</span>
                        <select value={subForm.target_operator || selectedCatalog.target.default_operator} onChange={(e) => setSubForm({ ...subForm, target_operator: e.target.value })}
                          className="px-2 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-white text-sm">
                          {selectedCatalog.target.operators.map(op => <option key={op} value={op}>{op}</option>)}
                        </select>
                        <input type="number" step="any" value={subForm.target_value} onChange={(e) => setSubForm({ ...subForm, target_value: e.target.value })}
                          placeholder={`value (${selectedCatalog.target.unit})`}
                          className="flex-1 min-w-0 px-2 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-white text-sm" />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button type="submit" className="px-4 py-1.5 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg text-sm">Add & rescore</button>
                      <button type="button" onClick={() => { setShowAddSub(null); setSubForm({ catalog_key: '', target_operator: '', target_value: '' }); }} className="px-3 py-1.5 text-gray-400 hover:text-white text-sm">Cancel</button>
                    </div>
                    {availableCatalog.length === 0 && <span className="text-xs text-gray-500">All scorable attributes are already in this buyer's criteria.</span>}
                  </form>
                ) : (
                  <div className="px-4 py-2">
                    <button onClick={() => { setShowAddSub(parent.id); setShowAddParent(false); setSubForm({ catalog_key: '', target_operator: '', target_value: '' }); }} className="text-gray-500 hover:text-[#00D4FF] text-xs">+ Add sub-criterion</button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
